import type { Client } from '@accounts/transport';
import type { Account } from './types';

/** The one password policy, as the service publishes it for the forms */
export interface Policy {
	min: number;
	max: number;
	breaches: boolean;
}

/** What signing in answered: a session, or the second step for an account with a second factor */
/** The AI apps and personal access tokens a choice to end what acts for the person ended (D196) */
export interface Ended {
	apps: number;
	tokens: number;
}

export interface Entered {
	account?: Account;
	created?: boolean;
	/** Sessions a recovery ended */
	ended?: number;
	/** What else a recovery ended, when the person chose it (D196) */
	access?: Ended;
	factor?: { ticket: string; methods: string[]; expires: string };
}

/**
 * Every password operation, through the service (ACC-02): signing in and up, the confirmation and
 * recovery messages, and a signed-in person's own password and address.
 *
 * A password is a parameter of one call and is never kept by this class. Each request that signs a
 * person in carries its own proof, so a bare `UNAUTHENTICATED` is the caller's to explain (`token`),
 * never the page's session's.
 */
export class Credentials {
	static WAIT = 60;

	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	async policy(): Promise<Policy> {
		return this.#client.get<Policy>('/v1/passwords/policy');
	}

	/**
	 * @param challenge A solved puzzle, after the service asked for one
	 */
	async signin(email: string, password: string, challenge?: { puzzle: string; nonce: string }): Promise<Entered> {
		return this.#client.post<Entered>('/v1/signin/password', { email, password, challenge }, { ended: 'token', replay: false });
	}

	/**
	 * @param back The confirmation address the message leads to (`Confirmation.address`)
	 * @returns `{confirmation: true}` when a message was sent (also for an address already registered)
	 */
	async signup(input: { email: string; password: string; name: string; locale: string; back: string; agreements?: unknown }): Promise<Entered & { confirmation?: boolean }> {
		return this.#client.post<Entered & { confirmation?: boolean }>('/v1/signup', input, { ended: 'token', replay: false });
	}

	/** Sends the confirmation message again; at most once a minute per address */
	async resend(email: string, back: string): Promise<{ sent: boolean; retry: number }> {
		return this.#client.post<{ sent: boolean; retry: number }>('/v1/signin/confirmation', { email, back }, { ended: 'token', replay: false });
	}

	/** Sends a recovery message; at most once a minute per address */
	async recover(email: string, back: string): Promise<{ sent: boolean; retry: number }> {
		return this.#client.post<{ sent: boolean; retry: number }>('/v1/recovery', { email, back }, { ended: 'token', replay: false });
	}

	/** Redeems a confirmation message's link, which signs the person in */
	async confirm(type: string, hash: string): Promise<Entered> {
		return this.#client.post<Entered>('/v1/confirmation', { type, token_hash: hash }, { ended: 'token', replay: false });
	}

	/**
	 * Sets a new password from a recovery message: by its token hash, or by the recovery token an older
	 * message returned in the address
	 *
	 * @param keep Keeps the account's other sessions; by default they end
	 * @param access Ends the access of the person's AI apps and access tokens too (D196)
	 */
	async reset(proof: { hash?: string; token?: string }, password: string, keep: boolean, access: boolean): Promise<Entered> {
		return this.#client.post<Entered>('/v1/recovery/password', { token_hash: proof.hash, token: proof.token, password, keep, ...(access ? { access: 'end' } : {}) }, { ended: 'token', replay: false });
	}

	/** Whether the signed-in account has a password, and the policy */
	async password(): Promise<{ password: boolean; providers: string[]; policy: Policy }> {
		return this.#client.get<{ password: boolean; providers: string[]; policy: Policy }>('/v1/account/password');
	}

	/** Changes the password; the other sessions end unless `keep`, and what acts for the person with `access` (D196) */
	async change(current: string, password: string, keep: boolean, access: boolean): Promise<{ ended: number; access?: Ended }> {
		return this.#client.post<{ ended: number; access?: Ended }>('/v1/account/password', { current, password, others: keep ? 'keep' : 'end', ...(access ? { access: 'end' } : {}) }, { replay: false });
	}

	/** Asks to change the sign-in address; it changes once the new one is confirmed */
	async email(email: string, password: string): Promise<{ pending: string }> {
		return this.#client.post<{ pending: string }>('/v1/account/email', { email, password }, { replay: false });
	}

	/** Adds a password to an account that signs in with GitHub or Google only, with a fresh provider token */
	async add(token: string, password: string): Promise<{ password: boolean }> {
		return this.#client.post<{ password: boolean }>('/v1/account/identities/email', { token, password }, { ended: 'token', replay: false });
	}
}
